import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import { Conversation } from '../../conversation.js';
import type { Exam, ExamState } from '../../exam.js';
import { Student } from '../../student.js';
import { Teacher } from '../../teacher.js';
import { resolveIsCurrentSessionStudent } from '../../utils/exam-role.js';
import { VALID_KINDNESS_VALUES } from '../set-teacher-grading-kindness.js';
import { resolveTeacherUserId } from './memory.tool.js';

const VALID_THEMES = ['light', 'dark', 'system'] as const;

/** Students see neither grading_kindness nor AI review strictness on the settings page, so the schema hides them too. */
export function changeSettingsToolDef(_examState: ExamState, role?: 'teacher' | 'student') {
  const isStudent = role === 'student';
  return {
    type: 'function',
    function: {
      name: 'change_settings',
      description:
        "Changes the user's app settings (the same ones as on the settings page). " +
        'Call this ONLY when the user explicitly asks to change one of these settings — never on your own initiative and never based on something you merely inferred. ' +
        'Only pass the settings the user wants to change. ' +
        (isStudent
          ? 'Other settings (e.g. e-mail or browser notifications, data export) are NOT available yet — do not call this tool for them, tell the user instead.'
          : 'Other settings (e.g. AI review strictness, e-mail or browser notifications, data export) are NOT available yet — do not call this tool for them, tell the user instead. ' +
            'grading_kindness is the general overall grading strictness — use it (not save_teacher_memory) when the teacher asks to grade more strictly/leniently in general; specific grading rules still belong in save_teacher_memory.'),
      parameters: {
        type: 'object',
        properties: {
          language: {
            type: 'string',
            enum: SUPPORTED_LANGUAGES.map(l => l.value),
            description: `Interface language. ${SUPPORTED_LANGUAGES.map(l => `"${l.value}" = ${l.label}`).join(', ')}.`,
          },
          theme: {
            type: 'string',
            enum: [...VALID_THEMES],
            description: 'Color theme: "light", "dark", or "system" (follows the operating system).',
          },
          ...(isStudent
            ? {}
            : {
                grading_kindness: {
                  type: 'string',
                  enum: [...VALID_KINDNESS_VALUES],
                  description: 'How kindly the AI grades: "lenient" (generous), "balanced" (default), "strict".',
                },
              }),
        },
        required: [],
      },
    },
  };
}

export async function handleChangeSettings(exam: Exam, toolCallArgs: any): Promise<object> {
  const language = SUPPORTED_LANGUAGES.some(l => l.value === toolCallArgs?.language)
    ? toolCallArgs.language
    : undefined;
  const theme = (VALID_THEMES as readonly string[]).includes(toolCallArgs?.theme) ? toolCallArgs.theme : undefined;
  const gradingKindness = (VALID_KINDNESS_VALUES as readonly string[]).includes(toolCallArgs?.grading_kindness)
    ? toolCallArgs.grading_kindness
    : undefined;

  if (!language && !theme && !gradingKindness) {
    return { content: 'SYSTEM INFO: Keine gültige Einstellung angegeben. Es wurde nichts geändert.' };
  }

  const userID = await resolveTeacherUserId(exam);
  if (!userID) {
    return { content: 'SYSTEM INFO: Einstellungen konnten nicht geändert werden (kein Nutzer gefunden).' };
  }
  const isStudent = await resolveIsCurrentSessionStudent(userID);

  const changed: string[] = [];
  const notes: string[] = [];

  if (language) {
    const collection = isStudent ? await getCollection(Student) : await getCollection(Teacher);
    await collection.updateOne({ userID }, { $set: { language } });

    // The conversation stores the language label (see generate-answer.ts); persistAndEmitFinalTurn
    // re-reads it, so this turn's final emit already carries the new language.
    const languageLabel = SUPPORTED_LANGUAGES.find(l => l.value === language)!.label;
    const conversationId = (exam as any)?.conversationId;
    if (conversationId) {
      await (
        await getRawCollection(Conversation._collectionName)
      ).updateOne({ _id: conversationId } as any, { $set: { language: languageLabel } });
    }
    changed.push(`Sprache → ${language}`);
    notes.push(`Antworte ab sofort auf ${languageLabel}.`);
  }

  if (theme) {
    changed.push(`Design → ${theme}`);
  }

  // Not in a student's schema; ignored silently if passed anyway, since students never see this setting.
  if (gradingKindness && !isStudent) {
    await (await getCollection(Teacher)).updateOne({ userID }, { $set: { gradingKindness } });
    changed.push(`Bewertungsstrenge → ${gradingKindness}`);
  }

  logger.info({ context: 'change_settings', userID, language, theme, gradingKindness, isStudent }, 'Settings changed');

  const content = [
    changed.length > 0
      ? `SYSTEM INFO: Einstellungen geändert: ${changed.join(', ')}.`
      : 'SYSTEM INFO: Nichts geändert.',
    ...notes,
  ].join(' ');

  // Language and theme also have to take effect in the browser — the client applies and clears this.
  const pendingSettingsChange = language || theme ? JSON.stringify({ language, theme }) : undefined;

  return { content, ...(pendingSettingsChange ? { pendingSettingsChange } : {}) };
}

export function changeSettingsAvailability(_examState: ExamState): boolean {
  return true;
}
